import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, InputGroup, Modal, Card } from "react-bootstrap";
import { validateEnclosedField } from "../../utils/validators";
import ObjectFieldSelector from "../fieldselector/ObjectFieldSelector";
import WorkflowAttachmentSelector from "./WorkflowAttachmentSelector";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import TemplateSearchDialog from "../watconnect/whatsapp_template/TemplateSearchDialog";
import DataApi from "../../api/DataApi";
export default function WorkflowSendWhatsApp({
  config = {},
  onChange,
  objectName,
  readOnly,
  selectedWhatsAppSetting = null, // Optional prop, will use sessionStorage if not provided
  workflowId,
  actionIndex,
  eventType,
}) {
  // Get WhatsApp setting from config (for saved workflows), prop, localStorage, or sessionStorage
  const whatsAppSetting = config.selectedWhatsAppSetting || selectedWhatsAppSetting || localStorage.getItem('selectedWhatsAppSetting') || sessionStorage.getItem('selectedWhatsAppSetting') || null;

  const [parameters, setParameters] = useState(config.parameters || []);
  const [recipientError, setRecipientError] = useState("");
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [attachmentRequired, setAttachmentRequired] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [phoneFields, setPhoneFields] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");

  const [fieldSelectorConfig, setFieldSelectorConfig] = useState({
    target: null,
    filterType: "all",
  });

  const [templateHeader, setTemplateHeader] = useState(config.templateHeader || config.header || "");

  const getHeaderMediaType = (headerStr, templateObj = {}) => {
    const header = (headerStr || "").toUpperCase();
    if (header === "DOCUMENT" || templateObj.header_document_url) return "DOCUMENT";
    if (header === "VIDEO" || templateObj.header_video_url) return "VIDEO";
    if (header === "IMAGE" || templateObj.header_image_url) return "IMAGE";
    return "";
  };

  // Fetch phone fields when objectName changes
  useEffect(() => {
    const fetchPhoneFields = async () => {
      if (!objectName) {
        setPhoneFields([]);
        return;
      }

      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (response.success && response.data?.fields) {
          // Filter to only phone data type fields
          const filteredFields = response.data.fields.filter(f => {
            const dataType = (f.data_type || '').toLowerCase();
            return dataType === 'phone';
          });
          setPhoneFields(filteredFields);
        } else {
          setPhoneFields([]);
        }
      } catch (error) {
        console.error("Error fetching phone fields:", error);
        setPhoneFields([]);
      }
    };

    fetchPhoneFields();
  }, [objectName]);

  useEffect(() => {
    setParameters(config.parameters || []);
  }, [config.parameters]);

  // Initialize WhatsApp setting in config if not already set
  useEffect(() => {
    // Only set if not already in config and we have a value from localStorage/sessionStorage
    if (!config.selectedWhatsAppSetting) {
      // Try to get from localStorage first (since user mentioned it's there), then sessionStorage
      const settingFromStorage = localStorage.getItem('selectedWhatsAppSetting') || sessionStorage.getItem('selectedWhatsAppSetting') || selectedWhatsAppSetting || null;
      if (settingFromStorage) {
        console.log('[WorkflowSendWhatsApp] Initializing selectedWhatsAppSetting in config:', settingFromStorage);
        onChange("selectedWhatsAppSetting", settingFromStorage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Fetch template name when templateId exists (for editing existing workflows)
  // First try to use templateName from config, otherwise fetch it
  useEffect(() => {
    if (config.templateName) {
      setSelectedTemplateName(config.templateName);
    }
    if (config.templateHeader || config.header) {
      const headerType = getHeaderMediaType(config.templateHeader || config.header);
      setTemplateHeader(headerType);
      if (headerType) {
        setAttachmentRequired(true);
      }
    }

    // Fetch from API to get full template header details
    const fetchTemplateDetails = async () => {
      if (config.templateId && whatsAppSetting) {
        try {
          const dataApi = new DataApi('watconnect');
          const result = await dataApi.fetchById(
            `watconnect_template?business_number=${whatsAppSetting}`
          );

          if (result?.data?.data && Array.isArray(result.data.data)) {
            const template = result.data.data.find(
              t => (t.template_id === config.templateId || t.id === config.templateId ||
                    t.template_id?.toString() === config.templateId?.toString() ||
                    t.id?.toString() === config.templateId?.toString())
            );

            if (template) {
              const templateName = template.template_name || template.templatename || "";
              setSelectedTemplateName(templateName);
              if (!config.templateName) {
                onChange("templateName", templateName);
              }

              const headerType = getHeaderMediaType(template.header, template);
              setTemplateHeader(headerType);
              if (headerType) {
                setAttachmentRequired(true);
                if (!config.templateHeader) {
                  onChange("templateHeader", headerType);
                }
              } else {
                setAttachmentRequired(false);
              }
            }
          }
        } catch (error) {
          console.error("Failed to fetch template details:", error);
        }
      } else if (!config.templateId) {
        // Clear template name if no template ID
        setSelectedTemplateName("");
        setTemplateHeader("");
        setAttachmentRequired(false);
      }
    };

    fetchTemplateDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.templateId, config.templateName, whatsAppSetting]);

  const handleFieldChange = (field, value) => {
    if (field === "recipientField") {
      const error = validateEnclosedField(value);
      setRecipientError(error);
    }

    onChange(field, value);
  };

  const handleRecipientFieldChange = (e) => {
    const fieldName = e.target.value;
    if (fieldName) {
      // Format as {{fieldname}}
      const mergeField = `{{${fieldName}}}`;
      const error = validateEnclosedField(mergeField);
      setRecipientError(error);
      onChange("recipientField", mergeField);
    } else {
      setRecipientError("");
      onChange("recipientField", "");
    }
  };

  // Extract field name from merge syntax like "{{fieldname}}"
  const getSelectedRecipientFieldName = () => {
    if (config?.recipientField && typeof config.recipientField === 'string' && config.recipientField.includes('{{')) {
      const match = config.recipientField.match(/\{\{(.+?)\}\}/);
      return match ? match[1].trim() : null;
    }
    return null;
  };

  const handleTemplateSelect = (template) => {
    // Store template ID and name
    const templateId = template.id || template.template_id;
    const templateName = template.name || template.template_name || "";
    onChange("templateId", templateId);
    onChange("templateName", templateName); // Store template name in config for persistence
    if (whatsAppSetting) {
      onChange("selectedWhatsAppSetting", whatsAppSetting); // Store WhatsApp setting in config
    }
    setSelectedTemplateName(templateName);

    // Extract example sample values if present (e.g., ["Rahul", "CRM Services", "Free Consultation"])
    let exampleTexts = [];
    if (Array.isArray(template.example_body_text)) {
      exampleTexts = template.example_body_text;
    } else if (typeof template.example_body_text === "string") {
      try {
        exampleTexts = JSON.parse(template.example_body_text);
      } catch (e) {
        exampleTexts = [];
      }
    }

    // Parse placeholders {{1}}, {{2}}, {{3}}, etc. from message_body or example_body_text
    const fullText = `${template.header_body || ""} ${template.message_body || ""} ${
      Array.isArray(exampleTexts) ? exampleTexts.join(" ") : ""
    }`;
    const paramRegex = /\{\{(\d+)\}\}/g;
    const paramMatches = new Set();
    let match;
    while ((match = paramRegex.exec(fullText)) !== null) {
      paramMatches.add(parseInt(match[1]));
    }

    if (paramMatches.size > 0) {
      const maxParam = Math.max(...Array.from(paramMatches));
      const generatedParams = Array.from({ length: maxParam }, (_, index) => {
        const pos = index + 1;
        const sampleText = Array.isArray(exampleTexts) && exampleTexts[index] ? exampleTexts[index] : "";
        return {
          position: pos,
          value: "",
          fallbackValue: sampleText || "",
          isActive: true,
          label: sampleText ? `Parameter {{${pos}}} (Sample: "${sampleText}")` : `Parameter {{${pos}}}`,
          sampleText,
        };
      });

      setParameters(generatedParams);
      onChange("parameters", generatedParams);
    } else {
      setParameters([]);
      onChange("parameters", []);
    }

    // Handle attachment requirement for DOCUMENT, IMAGE, or VIDEO headers
    const headerType = getHeaderMediaType(template.header, template);
    setTemplateHeader(headerType);
    onChange("templateHeader", headerType);

    if (headerType) {
      setAttachmentRequired(true);
      setAttachmentError(""); // reset error
    } else {
      setAttachmentRequired(false);
      setAttachmentFile(null); // clear file if no longer needed
    }
  };

  const addParameter = () => {
    const newParam = { position: "", value: "", isActive: true };
    const updated = [...parameters, newParam];
    setParameters(updated);
    onChange("parameters", updated);
  };

  const updateParameter = (index, key, value) => {
    const updated = [...parameters];
    updated[index][key] = value;
    setParameters(updated);
    onChange("parameters", updated);
  };

  const removeParameter = (index) => {
    const updated = parameters.filter((_, idx) => idx !== index);
    setParameters(updated);
    onChange("parameters", updated);
  };

  const openFieldSelector = (target, filterType = "all") => {
    setFieldSelectorConfig({ target, filterType });
    setShowFieldSelector(true);
  };

  const handleFieldSelected = (field) => {
    if (field?.field_name) {
      const mergeField = `{{${field.field_name}}}`;
      console.log("mergeField: ", mergeField);
      console.log("fieldSelectorConfig.target: ", fieldSelectorConfig.target);
      if (fieldSelectorConfig.target === "recipient") {
        handleFieldChange("recipientField", mergeField);
      } else if (typeof fieldSelectorConfig.target === "number") {
        const currentValue = parameters[fieldSelectorConfig.target].value || "";
        updateParameter(
          fieldSelectorConfig.target,
          "value",
          currentValue + mergeField
        );
      }
    }
    setShowFieldSelector(false);
  };

  return (
    <>
      <Row>
        <Col>
          <Form.Group>
            <Form.Label>Recipient Field</Form.Label>
            <Form.Select
              value={getSelectedRecipientFieldName() || ""}
              onChange={handleRecipientFieldChange}
              isInvalid={!!recipientError}
              required={!readOnly}
              disabled={readOnly}
              style={{ fontSize: '0.95rem', padding: '0.5rem 0.75rem', minHeight: '38px' }}
            >
              <option value="">-Select Phone Field-</option>
              {phoneFields.map((field) => (
                <option key={field.field_name} value={field.field_name}>
                  {field.field_label || field.field_name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {recipientError}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group>
            <Form.Label>WhatsApp Template</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Select a template..."
                value={selectedTemplateName}
                readOnly
                required={!readOnly && !config.templateId}
                disabled={readOnly}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowTemplateDialog(true)}
                disabled={readOnly || !whatsAppSetting}
                title={!whatsAppSetting ? "WhatsApp setting required" : "Search and select template"}
              >
                Select Template
              </Button>
            </InputGroup>
            {config.templateId && (
              <Form.Text className="text-muted">
                Template ID: {config.templateId}
              </Form.Text>
            )}
            {!whatsAppSetting && (
              <Form.Text className="text-danger">
                WhatsApp setting is required to select templates
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mt-2">
            <Form.Label>Is Active</Form.Label>
            <Form.Check
              type="checkbox"
              checked={config.isActive || false}
              onChange={(e) => handleFieldChange("isActive", e.target.checked)}
              disabled={readOnly}
            />
          </Form.Group>{" "}
        </Col>
      </Row>

      {/*<Button className="mt-2" onClick={addParameter} disabled={readOnly}>
        Add Parameter
      </Button>*/}
      {parameters && parameters.length > 0 && (
        <Card className="mt-3">
          <Card.Header className="d-flex justify-content-between align-items-center py-2">
            <strong>Template Dynamic Parameters Mapping</strong>
            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
              Map CRM object fields or enter static values for placeholders
            </span>
          </Card.Header>
          <Card.Body className="py-3">
            <div className="alert alert-light border py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
              💡 <strong>You can provide values using either option:</strong>
              <div className="mt-1">
                • <strong>CRM Field Mapping:</strong> Click <em>"Insert Field"</em> to insert merge fields like <code>{"{{name}}"}</code> or <code>{"{{company}}"}</code>.
              </div>
              <div>
                • <strong>Static Value:</strong> Directly type fixed text into the input field (e.g. <em>"CRM Automation Suite"</em> or <em>"Free Consultation"</em>).
              </div>
            </div>

            {parameters.map((param, index) => (
              <Row key={index} className="mb-2 align-items-center">
                <Col md={3} className="d-flex align-items-center">
                  <span className="badge bg-secondary me-2" style={{ fontSize: "0.85rem" }}>
                    {`{{${param.position || index + 1}}}`}
                  </span>
                  <span style={{ fontSize: "0.85rem", color: "#495057", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {param.sampleText ? `Sample: "${param.sampleText}"` : `Param ${param.position || index + 1}`}
                  </span>
                </Col>

                <Col md={5}>
                  <InputGroup size="sm">
                    <Form.Control
                      type="text"
                      placeholder={
                        param.sampleText
                          ? `Value / Field (e.g. {{name}})`
                          : "Enter static text or click Insert Field"
                      }
                      value={param.value || ""}
                      onChange={(e) =>
                        updateParameter(index, "value", e.target.value)
                      }
                      disabled={readOnly}
                      required={!readOnly}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={() => openFieldSelector(index, "all")}
                      disabled={readOnly}
                    >
                      Insert Field
                    </Button>
                  </InputGroup>
                </Col>

                <Col md={4}>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder={param.sampleText ? `Fallback (e.g., "${param.sampleText}")` : "Fallback value (required)"}
                    value={param.fallbackValue || ""}
                    onChange={(e) =>
                      updateParameter(index, "fallbackValue", e.target.value)
                    }
                    required={!readOnly}
                    disabled={readOnly}
                    title="Fallback value is mandatory. Used if mapped field is empty/null on the record."
                  />
                </Col>
              </Row>
            ))}
          </Card.Body>
        </Card>
      )}

      {attachmentRequired && (
        <Row className="mt-3">
          <Col md={12}>
            <WorkflowAttachmentSelector
              config={config.attachmentConfig || {}}
              onChange={(field, value) =>
                handleFieldChange("attachmentConfig", {
                  ...config.attachmentConfig,
                  [field]: value,
                })
              }
              readOnly={readOnly}
              workflowId={workflowId}
              actionIndex={actionIndex}
              eventType={eventType}
              requiredMediaType={templateHeader || config.templateHeader || config.header}
            />
            <Form.Text className="text-muted">
              {templateHeader === "DOCUMENT"
                ? "This template requires a document attachment (PDF, DOC, DOCX, XLS, PPT, TXT, CSV). Please upload or select the required file."
                : templateHeader === "VIDEO"
                ? "This template requires a video attachment (MP4, 3GP, MOV, AVI). Please upload or select the required file."
                : "This template requires an image attachment (JPG, JPEG, PNG, GIF, WEBP). Please upload or select the required file."}
            </Form.Text>
          </Col>
        </Row>
      )}

      <Modal
        show={showFieldSelector}
        onHide={() => setShowFieldSelector(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select a Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ObjectFieldSelector
            objectName={objectName}
            multiSelect={false}
            onSelect={handleFieldSelected}
            fieldTypeFilter={fieldSelectorConfig.filterType}
          />
        </Modal.Body>
      </Modal>

      <TemplateSearchDialog
        show={showTemplateDialog}
        onHide={() => setShowTemplateDialog(false)}
        onSelect={handleTemplateSelect}
        selectedWhatsAppSetting={whatsAppSetting}
        selectedTemplateId={config.templateId}
      />
    </>
  );
}
